import { create } from 'zustand';
import { supabase } from './lib/supabase';
import { readArtistToken } from './lib/supabase';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { VideoSubmission, Artist, AdCreative } from './types';
import { cache } from './utils/cache';
import { generateThumbnailFromUrl, updateSubmissionThumbnail } from './utils/video/generateThumbnailFromUrl';

// Utility functions
const isSupabaseStorageUrl = (url: string): boolean => {
  return url.includes('supabase.co/storage/v1/object/public/');
};

const deleteVideoFromStorage = async (url: string, client?: SupabaseClient<any>): Promise<void> => {
  // Extract file path from Supabase storage URL
  const urlParts = url.split('/storage/v1/object/public/');
  if (urlParts.length < 2) return;

  const [bucket, ...pathParts] = urlParts[1].split('/');
  const filePath = pathParts.join('/');

  const { error } = await (client || supabase).storage
    .from(bucket)
    .remove([filePath]);

  if (error) throw error;
};

interface StoreState {
  // Data
  submissions: VideoSubmission[];
  artists: Artist[];
  adCreatives: AdCreative[];
  
  // UI State
  loading: boolean;
  error: string | null;
  sidebarOpen: boolean;
  selectedArtistId: string | null;
  
  // Pagination
  submissionsPagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
  };
  
  adCreativesPagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
  };

  // Actions
  fetchSubmissions: (page?: number, limit?: number, filters?: {
    artistId?: string;
    type?: string;
    status?: string;
    includeArchived?: boolean;
  }, skipCache?: boolean, client?: SupabaseClient<any>) => Promise<void>;
  fetchArtists: (client?: SupabaseClient<any>) => Promise<void>;
  fetchAdCreatives: (page?: number, limit?: number, filters?: {
    artistId?: string;
    platform?: string;
    status?: string;
  }, skipCache?: boolean, client?: SupabaseClient<any>) => Promise<void>;
  
  addSubmission: (submission: Omit<VideoSubmission, 'id' | 'messages' | 'createdAt' | 'updatedAt'>, client?: SupabaseClient<any>) => Promise<{ data?: VideoSubmission; error?: Error }>;
  updateSubmission: (id: string, updates: Partial<VideoSubmission>, skipNotification?: boolean, client?: SupabaseClient<any>) => Promise<{ success: boolean; error?: Error }>;
  deleteSubmission: (id: string, client?: SupabaseClient<any>) => Promise<void>;

  // Artist Actions
  addArtist: (artistData: { name: string; whatsappGroupId?: string | null; avatarUrl?: string; archived?: boolean }) => Promise<void>;
  updateArtist: (id: string, updates: Partial<Artist>) => Promise<{ error?: Error }>;
  deleteArtist: (id: string) => Promise<void>;

  addMessage: (submissionId: string, message: string, isAdmin?: boolean, skipWhatsAppNotification?: boolean, client?: SupabaseClient<any>) => Promise<{ success: boolean; error?: Error }>;
  deleteMessage: (submissionId: string, messageId: string, client?: SupabaseClient<any>) => Promise<{ success: boolean; error?: Error }>;
  markMessagesAsRead: (submissionId: string, client?: SupabaseClient<any>) => Promise<void>;

  // Ad Creative Actions
  deleteAdCreative: (id: string, client?: SupabaseClient<any>) => Promise<void>;
  updateAdCreativeStatus: (id: string, status: string, rejectionReason?: string | null, client?: SupabaseClient<any>) => Promise<void>;
  archiveAdCreative: (id: string, client?: SupabaseClient<any>) => Promise<void>;
  setAdCreatives: (creatives: AdCreative[], client?: SupabaseClient<any>) => Promise<AdCreative[] | undefined>;
  handleMoveToAdCreatives: (submission: VideoSubmission, client?: SupabaseClient<any>) => Promise<void>;
  
  toggleSidebar: () => void;
  setSubmissionsPage: (page: number) => void;
  setAdCreativesPage: (page: number) => void;
  setSelectedArtistId: (artistId: string | null) => void;
  setArtists: (artists: Artist[]) => void;
  setSubmissions: (submissions: VideoSubmission[]) => void;
}

const useStore = create<StoreState>((set, get) => ({
  // Initial state
  submissions: [],
  artists: [],
  adCreatives: [],
  loading: false,
  error: null,
  sidebarOpen: typeof window !== 'undefined' ? window.innerWidth >= 768 : true, // Collapsed on mobile by default
  selectedArtistId: null,
  
  submissionsPagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 20,
  },
  
  adCreativesPagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 20,
  },

  // Fetch submissions
  fetchSubmissions: async (page = 1, limit = 20, filters = {}, skipCache = false, client?: SupabaseClient<any>) => {
    try {
      set({ loading: true, error: null });

      // Create cache key based on filters (v3 to invalidate old cache and include release data)
      const cacheKey = `submissions_v3_${page}_${limit}_${JSON.stringify(filters)}`;
      
      // Skip cache if explicitly requested (e.g., after status updates)
      if (!skipCache) {
        const cached = cache.get<any>(cacheKey);
        
        if (cached) {
          set({
            submissions: cached.submissions,
            submissionsPagination: cached.pagination,
            loading: false
          });
          return;
        }
      } else {
        // Invalidate cache when skipping
        cache.invalidate(cacheKey);
      }

      // Optimized query - only select necessary fields including messages and releases
      let query = (client || supabase)
        .from('submissions')
        .select(`
          id,
          project_name,
          video_url,
          artist_id,
          type,
          status,
          notes,
          created_at,
          updated_at,
          release_id,
          thumbnail_url,
          is_photo_post,
          image_urls,
          releases (
            id,
            name,
            release_date
          ),
          messages (
            id,
            text,
            is_admin,
            user_id,
            video_url,
            created_at,
            read_at,
            updated_at
          )
        `, { count: 'exact' });

      // Apply filters
      if (filters.artistId) {
        query = query.eq('artist_id' as any, filters.artistId as any);
      }
      if (filters.type) {
        query = query.eq('type' as any, filters.type as any);
      }
      if (filters.status) {
        query = query.eq('status' as any, filters.status as any);
      } else if (!filters.includeArchived) {
        // If no status filter is applied and includeArchived is not set, exclude archived videos by default
        query = query.neq('status' as any, 'archived' as any);
      }
      // If includeArchived is true, don't filter by status at all

      // Apply pagination and ordering
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to).order('created_at', { ascending: false });

      const { data, error, count } = await query;

      if (error) throw error;

      const transformedSubmissions: VideoSubmission[] = (data || []).map((sub: any) => {
        const messages = (sub.messages || []).map((msg: any) => ({
          id: msg.id,
          text: msg.text,
          isAdmin: msg.is_admin,
          userId: msg.user_id,
          videoUrl: msg.video_url,
          createdAt: msg.created_at,
          readAt: msg.read_at,
          updatedAt: msg.updated_at
        }));
        
        console.log(`📨 Loaded ${messages.length} messages for submission: ${sub.project_name}`);
        
        return {
          id: sub.id,
          projectName: sub.project_name,
          videoUrl: sub.video_url,
          artistId: sub.artist_id,
          type: sub.type,
          status: sub.status,
          notes: sub.notes,
          createdAt: sub.created_at,
          updatedAt: sub.updated_at,
          release_id: sub.release_id,
          releases: sub.releases,
          thumbnailUrl: sub.thumbnail_url,
          isPhotoPost: sub.is_photo_post,
          imageUrls: sub.image_urls,
          messages
        };
      });

      // Update pagination info
      const totalCount = count || 0;
      const totalPages = Math.ceil(totalCount / limit);

      const paginationData = {
        currentPage: page,
        pageSize: limit,
        totalPages,
        totalCount
      };

      // Cache the result for 1 minute
      cache.set(cacheKey, {
        submissions: transformedSubmissions,
        pagination: paginationData
      }, 60);

      set({
        submissions: transformedSubmissions,
        submissionsPagination: paginationData,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching submissions:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch submissions',
        loading: false 
      });
    }
  },

  // Fetch artists
  fetchArtists: async (client?) => {
    try {
      set({ error: null });
      const db = client ?? supabase;

      const { data, error } = await db
        .from('artists')
        .select('*')
        .order('name');

      if (error) throw error;

      // Fetch latest ad_creative date per artist
      const { data: adCreativeData } = await db.rpc('get_latest_ad_creative_per_artist' as any);
      const adCreativeMap = new Map<string, { lastDate: string; count: number }>();
      if (adCreativeData) {
        (adCreativeData as any[]).forEach((row: any) => {
          adCreativeMap.set(row.artist_id, { lastDate: row.last_ad_creative, count: Number(row.ad_creative_count) || 0 });
        });
      }

      const transformedArtists: Artist[] = (data || []).map((artist: any) => ({
        id: artist.id,
        name: artist.name,
        whatsappGroupId: artist.whatsapp_group_id,
        submissions: artist.submissions || 0,
        lastSubmission: artist.last_submission,
        lastAdCreative: adCreativeMap.get(artist.id)?.lastDate || null,
        adCreativeCount: adCreativeMap.get(artist.id)?.count || 0,
        archived: artist.archived || false,
        instagram_url: artist.instagram_url || null
      }));

      set({ artists: transformedArtists });
    } catch (error) {
      console.error('Error fetching artists:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to fetch artists' });
    }
  },

  // Fetch ad creatives. When called with no filters argument, reuse the
  // last filters that were explicitly supplied (stored on the hidden
  // `_adCreativesLastFilters` slot). This prevents call sites like
  // `fetchAdCreatives()` in VideoPreviewModal / AdCreativeSubmissionModal
  // from silently wiping the user's active artist/platform/status filters
  // and replacing the visible table with an unfiltered 4500-row list.
  fetchAdCreatives: async (page = 1, limit = 50, filters?: { artistId?: string; platform?: string; status?: string }, skipCache = false, client?) => {
    try {
      const db = client ?? supabase;
      const lastFilters = (get() as any)._adCreativesLastFilters as
        | { artistId?: string; platform?: string; status?: string }
        | undefined;
      const effectiveFilters = filters ?? lastFilters ?? {};
      // Bump a monotonic sequence on every call. The result handler
      // bails out if a newer call has started in the meantime — this
      // prevents a slow unfiltered fetch from racing past a fast
      // filtered one and overwriting the visible rows with stale data.
      const fetchSeq = (((get() as any)._adCreativesFetchSeq as number) || 0) + 1;
      set({ _adCreativesFetchSeq: fetchSeq } as any);
      const isStale = () =>
        ((get() as any)._adCreativesFetchSeq as number) !== fetchSeq;
      console.log(
        '🔍 fetchAdCreatives called with filters:',
        JSON.stringify(effectiveFilters),
        filters === undefined && lastFilters ? '(inherited from last call)' : '',
        `seq=${fetchSeq}`,
      );
      // Remember the filters whenever they're explicitly passed so future
      // no-arg calls can inherit them.
      if (filters !== undefined) {
        set({ _adCreativesLastFilters: filters } as any);
      }
      filters = effectiveFilters;

      // Create cache key based on filters
      const cacheKey = `ad_creatives_v1_${page}_${limit}_${JSON.stringify(filters)}`;

      // Check cache if not skipping
      if (!skipCache) {
        const cached = cache.get<any>(cacheKey);
        if (cached) {
          if (isStale()) return; // a newer call has superseded us
          set({
            adCreatives: cached.creatives,
            adCreativesPagination: cached.pagination,
            loading: false,
            error: null,
          });
          return;
        }
      } else {
        cache.invalidate(cacheKey);
      }

      // Cache miss → about to hit the network. Two cases:
      //
      //  a) Filters CHANGED since the last fetch → clear the current list
      //     so the UI shows Loading… instead of stale data from the prior
      //     (possibly unfiltered) filter set.
      //
      //  b) Filters UNCHANGED (background refresh after a thumbnail
      //     auto-fetch, audio scan, etc.) → keep the existing rows in
      //     place so the user's scroll position and row identities are
      //     preserved. The new result will simply replace the array once
      //     the network call resolves.
      const filtersChanged =
        JSON.stringify(lastFilters || {}) !== JSON.stringify(filters || {});
      if (filtersChanged) {
        // Visible loading state — UI replaces rows with a spinner/placeholder
        set({ loading: true, error: null, adCreatives: [] });
      } else {
        // Silent background refresh — leave loading=false and existing
        // rows in place so the table doesn't flicker or reset scroll.
        set({ error: null });
      }

      let query = db
        .from('ad_creatives')
        .select('*, releases(id, name, release_date, service_package, spotify_uri, cover_url, meta_campaign_id, meta_campaign_name, tiktok_campaign_id, tiktok_campaign_name, tiktok_campaign_status, release_campaign_links(smartlink_url, smartlink_id, report_id))', { count: 'exact' });

      // Apply filters
      if (filters.artistId) {
        console.log('🎯 Applying artistId filter:', filters.artistId, 'type:', typeof filters.artistId);
        query = query.eq('artists_id' as any, filters.artistId as any);
      }
      if (filters.platform) {
        if (filters.platform === 'instagram') {
          // Broad "Instagram" match: native platform='instagram' rows OR
          // any creative (dropbox / direct_upload / etc.) that carries a
          // merged Instagram reel URL — these are effectively IG reels
          // that got mirrored into Supabase storage but kept their
          // platform column as 'direct_upload'.
          query = query.or(
            "platform.eq.instagram,merged_instagram_reel_url.not.is.null" as any,
          );
        } else if (filters.platform === 'tiktok') {
          // Analogous broad match for TikTok: native platform='tiktok'
          // rows OR any creative carrying a merged TikTok auth code.
          query = query.or(
            "platform.eq.tiktok,merged_tiktok_auth_code.not.is.null" as any,
          );
        } else if (filters.platform === 'video_files') {
          // "Video Files" = directly uploaded videos (dropbox legacy +
          // direct_upload). Both are file-based creatives that aren't
          // tied to an Instagram/TikTok platform URL.
          query = query.in('platform' as any, ['dropbox', 'direct_upload'] as any);
        } else {
          query = query.eq('platform' as any, filters.platform as any);
        }
      }
      if (filters.status) {
        query = query.eq('status' as any, filters.status as any);
      } else {
        // If no status filter is selected, exclude archived items by default
        query = query.neq('status' as any, 'archived' as any);
      }

      // Log the query URL for debugging
      console.log('🔍 Query filters applied - artistId:', filters.artistId, 'platform:', filters.platform, 'status:', filters.status);
      
      // Sort by release date desc (newest release first) when artist filter is active.
      // Tiebreaker by release name so creatives belonging to the same release
      // stay together even when multiple releases share a release_date
      // (e.g. regional duplicates of one campaign — DACH, UK, US, …).
      if (filters.artistId) {
        query = query
          .order('releases(release_date)', { ascending: false, nullsFirst: false })
          .order('releases(name)', { ascending: true, nullsFirst: false })
          .order('created_at', { ascending: false });
      } else {
        // In admin view: just sort by date, we'll handle status priority client-side
        query = query.order('created_at', { ascending: false });
      }
      
      // In admin view without artist filter, fetch all data to sort by status client-side
      // This ensures pending items appear first across all pages
      let data, error, count;
      if (!filters.artistId) {
        // Fetch all data
        const result = await query;
        data = result.data;
        error = result.error;
        count = result.count;
        
        if (data && !error) {
          // Sort by status (pending first) then by upcoming release, then by date
          data.sort((a: any, b: any) => {
            const aIsPending = a.status === 'pending';
            const bIsPending = b.status === 'pending';
            
            if (aIsPending && !bIsPending) return -1;
            if (!aIsPending && bIsPending) return 1;
            
            // Both are pending - check if they have upcoming release dates
            if (aIsPending && bIsPending) {
              const aReleaseDate = a.releases?.release_date;
              const bReleaseDate = b.releases?.release_date;
              const now = new Date();
              
              const aHasUpcomingRelease = aReleaseDate && new Date(aReleaseDate) > now;
              const bHasUpcomingRelease = bReleaseDate && new Date(bReleaseDate) > now;
              
              // Prioritize pending WITHOUT upcoming release (or no release, or past release)
              if (aHasUpcomingRelease && !bHasUpcomingRelease) return 1;  // a goes after b
              if (!aHasUpcomingRelease && bHasUpcomingRelease) return -1; // a goes before b
            }
            
            // Same status and upcoming status - sort by date
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          });
          
          // Apply pagination client-side
          const from = (page - 1) * limit;
          const to = from + limit;
          data = data.slice(from, to);
        }
      } else {
        // With artist filter, use server-side pagination
        const from = (page - 1) * limit;
        const to = from + limit - 1;
        const result = await query.range(from, to);
        data = result.data;
        error = result.error;
        count = result.count;
      }

      console.log('🔍 fetchAdCreatives result - count:', count, 'data length:', data?.length, `seq=${fetchSeq}`, isStale() ? '(STALE — discarded)' : '');
      if (data && data.length > 0) {
        console.log('🔍 Sample artists_id values from results:', data.slice(0, 3).map((d: any) => d.artists_id));
      }
      if (error) {
        console.error('🔍 fetchAdCreatives error:', error);
        throw error;
      }
      // Drop the result if a newer fetch has been kicked off — this
      // prevents stale data from clobbering the visible rows.
      if (isStale()) return;

      const transformedCreatives: AdCreative[] = (data || []).map((creative: any) => ({
        id: creative.id,
        createdAt: creative.created_at,
        artists_id: creative.artists_id,
        platform: creative.platform,
        content: creative.content,
        status: creative.status,
        rejectionReason: creative.rejection_reason,
        updatedAt: creative.updated_at,
        videoName: creative.video_name,
        mergedInstagramReelUrl: creative.merged_instagram_reel_url,
        mergedTiktokAuthCode: creative.merged_tiktok_auth_code,
        submissionId: creative.submission_id,
        thumbnail_url: creative.thumbnail_url,
        instagram_thumbnail_url: creative.instagram_thumbnail_url,
        tiktok_thumbnail_url: creative.tiktok_thumbnail_url,
        audio_type: (creative as any).audio_type,
        original_instagram_url: (creative as any).original_instagram_url,
        release_id: creative.release_id,
        releases: creative.releases,
        instagram_url_updated_at: creative.instagram_url_updated_at,
        tiktok_url_updated_at: creative.tiktok_url_updated_at,
        last_activated_at: creative.last_activated_at,
        // Campaign-link fields — needed by the UI to compute
        // pending/linked states for per-row pill buttons and the
        // release-divider bulk-add counts.
        meta_ad_id: (creative as any).meta_ad_id,
        meta_ad_status: (creative as any).meta_ad_status,
        tiktok_ad_id: (creative as any).tiktok_ad_id,
        tiktok_campaign_id: (creative as any).tiktok_campaign_id,
        tiktok_adgroup_id: (creative as any).tiktok_adgroup_id,
        tiktok_ad_status: (creative as any).tiktok_ad_status,
        // Permanently mirrored TikTok Spark Ad media (videos/thumbnails
        // buckets). Needed by the row Download button so the UI can
        // offer a stable download link even after TikTok CDN URLs expire.
        tiktok_post_video_stored_url: (creative as any).tiktok_post_video_stored_url,
        tiktok_post_poster_stored_url: (creative as any).tiktok_post_poster_stored_url,
      }));

      const totalCount = count || 0;
      const totalPages = Math.ceil(totalCount / limit);

      const paginationData = {
        currentPage: page,
        totalPages,
        totalCount,
        pageSize: limit,
      };

      // Cache the result for 1 minute
      cache.set(cacheKey, {
        creatives: transformedCreatives,
        pagination: paginationData
      }, 60);

      set({
        adCreatives: transformedCreatives,
        adCreativesPagination: paginationData,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching ad creatives:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch ad creatives',
        loading: false 
      });
    }
  },

  // Add submission
  addSubmission: async (submission: Omit<VideoSubmission, 'id' | 'messages' | 'createdAt' | 'updatedAt'>, client?: SupabaseClient<any>) => {
    try {
      set({ loading: true, error: null });
      const usingArtistClient = !!client;
      console.log('[addSubmission] Starting', {
        projectName: submission.projectName,
        artistId: submission.artistId,
        type: submission.type,
        videoUrl: submission.videoUrl,
        usingArtistClient,
      });

      const { data, error } = await (client || supabase)
        .from('submissions')
        .insert({
          project_name: submission.projectName,
          video_url: submission.videoUrl,
          artist_id: submission.artistId,
          type: submission.type,
          status: submission.status,
          notes: submission.notes,
          release_id: (submission as any).release_id || null,
          is_photo_post: (submission as any).isPhotoPost || false,
          image_urls: (submission as any).imageUrls || null,
        } as any)
        .select(`
          id,
          project_name,
          video_url,
          artist_id,
          type,
          status,
          notes,
          release_id,
          is_photo_post,
          image_urls,
          created_at,
          updated_at,
          releases(id, name, release_date)
        `)
        .single();

      if (error) {
        console.error('[addSubmission] DB insert failed', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          usingArtistClient,
        });
        // Handle unique constraint violations
        if (error.code === '23505') {
          if (error.message.includes('submissions_pkey')) {
            throw new Error('duplicate_submission_id');
          }
          // Handle other unique constraints if they exist
          throw new Error('duplicate_entry');
        }
        throw error;
      }

      if (!data) {
        console.error('[addSubmission] No data returned from insert');
        throw new Error('No data returned from submission creation');
      }

      console.log('[addSubmission] Success', {
        id: (data as any).id,
        project_name: (data as any).project_name,
        artist_id: (data as any).artist_id,
      });

      const transformedSubmission: VideoSubmission = {
        id: (data as any).id,
        projectName: (data as any).project_name,
        videoUrl: (data as any).video_url,
        artistId: (data as any).artist_id,
        type: (data as any).type,
        status: (data as any).status,
        notes: (data as any).notes,
        createdAt: (data as any).created_at,
        updatedAt: (data as any).updated_at,
        isPhotoPost: (data as any).is_photo_post,
        imageUrls: (data as any).image_urls,
        messages: [],
        ...(data as any).release_id && { release_id: (data as any).release_id },
        ...(data as any).releases && { releases: (data as any).releases }
      } as any;

      // Add to store
      set(state => ({
        submissions: [transformedSubmission, ...state.submissions],
        loading: false
      }));

      // Send WhatsApp notification
      try {
        const artist = get().artists.find(a => a.id === submission.artistId);
        if (artist) {
          // WhatsApp notifications would be sent here
          console.log('Would send WhatsApp notification for submission update:', transformedSubmission.id);
        }
      } catch (notifyError) {
        console.error('Error sending WhatsApp notification:', notifyError);
      }

      // Auto-generate thumbnail for supported videos in background
      const url = submission.videoUrl;
      const isOurStorage = url.includes('wrlgoxbzlngdtomjhvnz.supabase.co/storage/v1/object/public/videos');
      const isSupportedFormat = url.match(/\.(mp4|webm)(\?|$)/i);
      
      if (isOurStorage && isSupportedFormat) {
        // Generate thumbnail in background without blocking
        generateThumbnailFromUrl(url).then(async (thumbnailUrl) => {
          if (thumbnailUrl) {
            try {
              await updateSubmissionThumbnail(transformedSubmission.id.toString(), thumbnailUrl);
              // Update the submission in store with the thumbnail
              set(state => ({
                submissions: state.submissions.map(s => 
                  s.id === transformedSubmission.id 
                    ? { ...s, thumbnailUrl } 
                    : s
                )
              }));
            } catch (error) {
              console.error('Error updating submission thumbnail:', error);
            }
          }
        }).catch(error => {
          console.error('Error generating thumbnail for new submission:', error);
        });
      }

      return { data: transformedSubmission };
    } catch (error) {
      console.error('Error adding submission:', error);
      set({ loading: false });
      return { error: error instanceof Error ? error : new Error('Failed to add submission') };
    }
  },

  // Update submission
  updateSubmission: async (id: string, updates: Partial<VideoSubmission>, skipNotification = false, client?: SupabaseClient<any>) => {
    try {
      set({ loading: true, error: null });

      const updateData: any = {};
      if (updates.projectName !== undefined) updateData.project_name = updates.projectName;
      if (updates.videoUrl !== undefined) updateData.video_url = updates.videoUrl;
      if (updates.type !== undefined) updateData.type = updates.type;
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.notes !== undefined) updateData.notes = updates.notes;
      if ((updates as any).release_id !== undefined) updateData.release_id = (updates as any).release_id;

      updateData.updated_at = new Date().toISOString();

      const { data, error } = await (client || supabase)
        .from('submissions')
        .update(updateData)
        .eq('id' as any, id as any)
        .select(`
          id,
          project_name,
          video_url,
          artist_id,
          type,
          status,
          notes,
          created_at,
          updated_at,
          release_id,
          thumbnail_url,
          is_photo_post,
          image_urls,
          releases (
            id,
            name,
            release_date
          ),
          messages (
            id,
            text,
            is_admin,
            user_id,
            video_url,
            created_at,
            read_at,
            updated_at
          )
        `)
        .single();

      if (error) throw error;

      if (!data) {
        throw new Error('No data returned from submission update');
      }

      const transformedSubmission: VideoSubmission = {
        id: (data as any).id,
        projectName: (data as any).project_name,
        videoUrl: (data as any).video_url,
        artistId: (data as any).artist_id,
        type: (data as any).type,
        status: (data as any).status,
        notes: (data as any).notes,
        createdAt: (data as any).created_at,
        updatedAt: (data as any).updated_at,
        thumbnailUrl: (data as any).thumbnail_url ?? undefined,
        isPhotoPost: (data as any).is_photo_post,
        imageUrls: (data as any).image_urls,
        ...(data as any).release_id && { release_id: (data as any).release_id },
        ...(data as any).releases && { releases: (data as any).releases },
        messages: ((data as any).messages || []).map((msg: any) => ({
          id: msg.id,
          text: msg.text,
          isAdmin: msg.is_admin,
          userId: msg.user_id,
          videoUrl: msg.video_url,
          createdAt: msg.created_at,
          readAt: msg.read_at,
          updatedAt: msg.updated_at
        }))
      };

      set(state => ({
        submissions: state.submissions.map(sub =>
          sub.id.toString() === id.toString() ? { ...sub, ...transformedSubmission } : sub
        ),
        loading: false
      }));

      // Invalidate cache if status changed (especially for archived videos)
      if (updates.status) {
        cache.clearAll();
      }

      // Refresh ad creatives if status changed to ready (database trigger handles creation)
      if (updates.status === 'ready' && transformedSubmission.type === 'song-specific') {
        console.log('🎯 Video set to ready, database trigger will handle ad creative creation:', transformedSubmission.projectName);
        
        // Refresh ad creatives to show any created by database trigger
        try {
          await get().fetchAdCreatives();
        } catch (refreshError) {
          console.error('Error refreshing ad creatives:', refreshError);
        }
      }

      // Send WhatsApp notification if not skipped
      if (!skipNotification) {
        try {
          console.log('🔔 [updateSubmission] Attempting to send WhatsApp notification...');
          const artist = get().artists.find(a => a.id === transformedSubmission.artistId);
          if (artist) {
            const { WhatsAppService } = await import('./services/whatsapp');
            // Always notify the artist (their WhatsApp group) about status changes
            // The notifyArtist function will handle team notifications internally if needed
            await WhatsAppService.notifyArtist({
              artist,
              submission: transformedSubmission,
              feedback: updates.status ? `Status updated to: ${transformedSubmission.status}` : undefined,
              status: transformedSubmission.status
            });
            console.log('✅ [updateSubmission] WhatsApp notification sent');
          }
        } catch (notifyError) {
          console.error('❌ [updateSubmission] Error sending WhatsApp notification:', notifyError);
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating submission:', error);
      set({ loading: false });
      return { 
        success: false, 
        error: error instanceof Error ? error : new Error('Failed to update submission') 
      };
    }
  },

  // Delete submission
  deleteSubmission: async (id: string, client?: SupabaseClient<any>) => {
    try {
      set({ loading: true, error: null });
      const usingArtistClient = !!client;

      // Get submission details before deletion for storage cleanup
      const submission = get().submissions.find(s => s.id.toString() === id);
      console.log('[deleteSubmission] Starting', {
        id,
        usingArtistClient,
        submissionFound: !!submission,
        videoUrl: submission?.videoUrl,
        artistId: submission?.artistId,
      });

      const { error, count } = await (client || supabase)
        .from('submissions')
        .delete()
        .eq('id' as any, id as any);

      if (error) {
        console.error('[deleteSubmission] DB delete failed', {
          id,
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          usingArtistClient,
        });
        throw error;
      }

      console.log('[deleteSubmission] DB delete success', { id, count });

      // Clean up video from storage if it's a Supabase storage URL
      if (submission?.videoUrl && isSupabaseStorageUrl(submission.videoUrl)) {
        try {
          await deleteVideoFromStorage(submission.videoUrl, client);
          console.log('[deleteSubmission] Storage cleanup success', { id, videoUrl: submission.videoUrl });
        } catch (storageError) {
          console.error('[deleteSubmission] Storage cleanup failed (non-fatal)', {
            id,
            videoUrl: submission.videoUrl,
            error: storageError instanceof Error ? storageError.message : storageError,
          });
        }
      }

      set(state => ({
        submissions: state.submissions.filter(sub => sub.id.toString() !== id),
        loading: false
      }));

      // Clear cache to refresh pagination counts
      cache.clearAll();
      console.log('[deleteSubmission] Complete', { id });
    } catch (error) {
      console.error('[deleteSubmission] Failed', {
        id,
        error: error instanceof Error ? error.message : error,
      });
      set({
        error: error instanceof Error ? error.message : 'Failed to delete submission',
        loading: false
      });
    }
  },

  // Add artist
  addArtist: async (artistData: { name: string; whatsappGroupId?: string | null; avatarUrl?: string; archived?: boolean }) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await supabase
        .from('artists')
        .insert({
          name: artistData.name,
          whatsapp_group_id: artistData.whatsappGroupId,
          archived: artistData.archived || false
        } as any)
        .select()
        .single();

      if (error) {
        // Handle unique constraint violations
        if (error.code === '23505') {
          if (error.message.includes('artists_pkey')) {
            throw new Error('duplicate_artist_id');
          }
          // Handle other unique constraints if they exist
          throw new Error('duplicate_entry');
        }
        throw error;
      }

      if (!data) {
        throw new Error('No data returned from artist creation');
      }

      const transformedArtist: Artist = {
        id: (data as any).id,
        name: (data as any).name,
        whatsappGroupId: (data as any).whatsapp_group_id,
        submissions: (data as any).submissions || 0,
        lastSubmission: (data as any).last_submission,
        archived: (data as any).archived || false
      };

      set(state => ({
        artists: [...state.artists, transformedArtist],
        loading: false
      }));
    } catch (error) {
      console.error('Error adding artist:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to add artist',
        loading: false 
      });
      throw error;
    }
  },

  // Update artist
  updateArtist: async (id: string, updates: Partial<Artist>) => {
    try {
      const { data, error } = await supabase
        .from('artists')
        .select(`
          id,
          name,
          whatsapp_group_id,
          submissions,
          last_submission,
          archived
        `)
        .eq('id' as any, id as any)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Artist not found');

      const updateData: any = {};
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.whatsappGroupId !== undefined) updateData.whatsapp_group_id = updates.whatsappGroupId;
      if (updates.archived !== undefined) updateData.archived = updates.archived;

      const { error: updateError } = await supabase
        .from('artists')
        .update(updateData)
        .eq('id' as any, id as any);

      if (updateError) throw updateError;

      set(state => ({
        artists: state.artists.map(artist => 
          artist.id.toString() === id.toString() 
            ? { ...artist, ...updates }
            : artist
        )
      }));

      return {};
    } catch (error) {
      console.error('Error updating artist:', error);
      return { error: error instanceof Error ? error : new Error('Failed to update artist') };
    }
  },

  // Delete artist
  deleteArtist: async (id: string) => {
    try {
      set({ loading: true, error: null });

      // Deleting the artist cascades to releases (ON DELETE CASCADE),
      // which in turn cascades to project_tracking, and all other related
      // tables (submissions, ad_creatives, contacts, etc. also CASCADE).
      const { error } = await supabase
        .from('artists')
        .delete()
        .eq('id' as any, id as any);

      if (error) throw error;

      set(state => ({
        artists: state.artists.filter(artist => artist.id.toString() !== id),
        loading: false
      }));
    } catch (error) {
      console.error('Error deleting artist:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to delete artist',
        loading: false
      });
    }
  },

  // Update feedback
  addMessage: async (submissionId: string, message: string, isAdmin = false, skipWhatsAppNotification = false, client?: SupabaseClient<any>) => {
    try {
      // Get current user ID from auth context
      // Try the provided client first (artist-scoped), fall back to global
      const { data: { user } } = await (client || supabase).auth.getUser();
      console.log('Current authenticated user:', user);
      console.log('User ID from auth:', user?.id);

      // Use the authenticated user's ID when available.
      // For protected artists (artist-scoped client, no global auth),
      // fall back to the user_id from the artist token.
      const artistToken = readArtistToken();
      const userId = user?.id || artistToken?.user_id || null;

      console.log('Final userId for message:', userId);

      const { data, error } = await (client || supabase)
        .from('messages')
        .insert({
          submission_id: submissionId,
          text: message,
          is_admin: isAdmin,
          user_id: userId, // may be null for public artist view
          updated_at: new Date().toISOString()
        } as any)
        .select(`
          id,
          text,
          is_admin,
          user_id,
          video_url,
          created_at,
          read_at,
          updated_at
        `)
        .single();

      if (error) throw error;
      if (!data) throw new Error('No data returned from message creation');

      console.log('Debug - Raw data from database:', data);
      console.log('Debug - user_id from database:', (data as any).user_id);

      const transformedMessage = {
        id: (data as any).id,
        text: (data as any).text,
        isAdmin: (data as any).is_admin,
        userId: (data as any).user_id,
        videoUrl: (data as any).video_url,
        createdAt: (data as any).created_at,
        readAt: (data as any).read_at,
        updatedAt: (data as any).updated_at
      };

      // Ensure userId is properly set
      if (!transformedMessage.userId) {
        console.error('Warning: Message created without userId!');
        transformedMessage.userId = userId;
      }

      console.log('Debug - Transformed message:', transformedMessage);

      set(state => {
        console.log('🔄 Store: Updating submissions with new message');
        console.log('Looking for submission ID:', submissionId);
        console.log('Current submissions count:', state.submissions.length);
        
        const updatedSubmissions = state.submissions.map(sub => {
          if (sub.id.toString() === submissionId.toString()) {
            console.log('✅ Found matching submission:', sub.projectName);
            console.log('Current messages count:', sub.messages.length);
            console.log('Adding new message:', transformedMessage);
            const updated = { ...sub, messages: [...sub.messages, transformedMessage] };
            console.log('Updated messages count:', updated.messages.length);
            return updated;
          }
          return sub;
        });
        
        console.log('🔄 Store: State update complete');
        return { submissions: updatedSubmissions };
      });

      // Send WhatsApp notification for feedback
      if (isAdmin && !skipWhatsAppNotification) {
        try {
          console.log('🔔 [addMessage] Attempting to send WhatsApp notification for feedback...');
          const submission = get().submissions.find(s => s.id.toString() === submissionId.toString());
          const artist = get().artists.find(a => a.id === submission?.artistId);
          
          console.log('🔔 [addMessage] Found submission:', submission?.projectName);
          console.log('🔔 [addMessage] Found artist:', artist?.name);
          console.log('🔔 [addMessage] Artist WhatsApp Group ID:', artist?.whatsappGroupId);
          
          if (artist && submission) {
            const { WhatsAppService } = await import('./services/whatsapp');
            await WhatsAppService.notifyArtist({
              artist,
              submission,
              feedback: message,
              status: submission.status
            });
            console.log('✅ [addMessage] WhatsApp notification sent for feedback on:', submission.projectName);
          } else {
            console.error('❌ [addMessage] Cannot send WhatsApp notification - missing artist or submission');
          }
        } catch (notifyError) {
          console.error('❌ [addMessage] Error sending WhatsApp notification for feedback:', notifyError);
        }
      } else if (skipWhatsAppNotification) {
        console.log('⚠️ [addMessage] Skipping WhatsApp notification - explicitly disabled');
      } else {
        console.log('⚠️ [addMessage] Skipping WhatsApp notification - not admin message');
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating feedback:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error : new Error('Failed to update feedback') 
      };
    }
  },

  // Delete message
  deleteMessage: async (submissionId: string, messageId: string, client?: SupabaseClient<any>) => {
    try {
      const { error } = await (client || supabase)
        .from('messages')
        .delete()
        .eq('id' as any, messageId as any)
        .eq('submission_id' as any, submissionId as any);

      if (error) throw error;

      set(state => ({
        submissions: state.submissions.map(sub => 
          sub.id.toString() === submissionId.toString()
            ? { ...sub, messages: sub.messages.filter(msg => msg.id.toString() !== messageId.toString()) }
            : sub
        )
      }));

      return { success: true };
    } catch (error) {
      console.error('Error deleting message:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error : new Error('Failed to delete message') 
      };
    }
  },

  // Mark messages as read
  markMessagesAsRead: async (submissionId: string, client?: SupabaseClient<any>) => {
    try {
      const { error } = await (client || supabase)
        .from('messages')
        .update({ read_at: new Date().toISOString() } as any)
        .eq('submission_id' as any, submissionId as any)
        .is('read_at', null);

      if (error) throw error;

      set(state => ({
        submissions: state.submissions.map(sub => 
          sub.id.toString() === submissionId.toString()
            ? { 
                ...sub, 
                messages: sub.messages.map(msg => ({ 
                  ...msg, 
                  readAt: msg.readAt || new Date().toISOString() 
                }))
              }
            : sub
        )
      }));
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  },

  // Add ad creatives
  setAdCreatives: async (creatives: AdCreative[], client?: SupabaseClient<any>) => {
    try {
      set({ loading: true, error: null });

      const { data, error } = await (client || supabase)
        .from('ad_creatives')
        .insert(creatives as any)
        .select();

      if (error) throw error;
      if (!data) throw new Error('No data returned from ad creatives creation');

      const transformedCreatives: AdCreative[] = (data as any[]).map((creative: any) => ({
        id: creative.id,
        createdAt: creative.created_at,
        artists_id: creative.artists_id,
        platform: creative.platform,
        content: creative.content,
        status: creative.status,
        rejectionReason: creative.rejection_reason,
        updatedAt: creative.updated_at,
        videoName: creative.video_name,
        mergedInstagramReelUrl: creative.merged_instagram_reel_url,
        mergedTiktokAuthCode: creative.merged_tiktok_auth_code,
        submissionId: creative.submission_id,
        thumbnail_url: creative.thumbnail_url,
        instagram_thumbnail_url: creative.instagram_thumbnail_url,
        tiktok_thumbnail_url: creative.tiktok_thumbnail_url,
        audio_type: (creative as any).audio_type,
        original_instagram_url: (creative as any).original_instagram_url,
        release_id: creative.release_id,
      }));

      // Only update global state if we're in the main admin view
      // Artist views should re-fetch to maintain proper filtering
      set(state => {
        // Check if current state appears to be filtered (small count suggests filtering)
        const appearsFiltered = state.adCreatives.length < 100; // Reasonable threshold
        
        if (appearsFiltered) {
          // Don't pollute filtered state - let components re-fetch
          console.log('Store: Skipping global state update - appears to be filtered view');
          return { loading: false };
        } else {
          // Update global state for main admin view
          return {
            adCreatives: [...transformedCreatives, ...state.adCreatives],
            loading: false
          };
        }
      });

      // Return the created creatives so components can handle them appropriately
      return transformedCreatives;
    } catch (error) {
      console.error('Error adding ad creatives:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to add ad creatives',
        loading: false 
      });
    }
  },

  // Update ad creative status
  updateAdCreativeStatus: async (id: string, status: string, rejectionReason: string | null = null, client?: SupabaseClient<any>) => {
    try {
      const updateData: any = {
        status,
        updated_at: new Date().toISOString()
      };

      if (rejectionReason) {
        updateData.rejection_reason = rejectionReason;
      }

      const { data, error } = await (client || supabase)
        .from('ad_creatives')
        .update(updateData as any)
        .eq('id' as any, id as any)
        .select();

      if (error) throw error;
      if (!data || data.length === 0) throw new Error('No data returned from ad creative update');

      const updatedCreative = (data as any[])[0];
      const transformedCreative: AdCreative = {
        id: updatedCreative.id,
        createdAt: updatedCreative.created_at,
        artists_id: updatedCreative.artists_id,
        platform: updatedCreative.platform,
        content: updatedCreative.content,
        status: updatedCreative.status,
        rejectionReason: updatedCreative.rejection_reason,
        updatedAt: updatedCreative.updated_at,
        videoName: updatedCreative.video_name,
        mergedInstagramReelUrl: updatedCreative.merged_instagram_reel_url,
        mergedTiktokAuthCode: updatedCreative.merged_tiktok_auth_code,
        submissionId: updatedCreative.submission_id,
        thumbnail_url: updatedCreative.thumbnail_url,
        instagram_thumbnail_url: updatedCreative.instagram_thumbnail_url,
        tiktok_thumbnail_url: updatedCreative.tiktok_thumbnail_url,
        audio_type: (updatedCreative as any).audio_type,
        original_instagram_url: (updatedCreative as any).original_instagram_url,
        release_id: updatedCreative.release_id,
        instagram_url_updated_at: updatedCreative.instagram_url_updated_at,
        tiktok_url_updated_at: updatedCreative.tiktok_url_updated_at,
        last_activated_at: updatedCreative.last_activated_at
      };

      set(state => ({
        adCreatives: state.adCreatives.map(creative => 
          creative.id === id ? transformedCreative : creative
        )
      }));

      // Send WhatsApp notification for status changes
      try {
        const { artists } = get();
        const artist = artists.find(a => a.id === updatedCreative.artists_id);
        
        if (artist && artist.whatsappGroupId) {
          // Import WhatsAppService dynamically
          const { WhatsAppService } = await import('./services/whatsapp');
          
          await WhatsAppService.notifyAdCreativeUpdate({
            artistName: artist.name,
            artistId: String(artist.id),
            artistGroupId: artist.whatsappGroupId,
            platform: updatedCreative.platform,
            content: updatedCreative.content,
            status: updatedCreative.status,
            rejectionReason: rejectionReason || undefined,
            videoName: updatedCreative.video_name
          });
          
          console.log('✅ WhatsApp notification sent for ad creative status change');
        } else {
          console.log('⚠️ No WhatsApp group ID found for artist, skipping notification');
        }
      } catch (notifyError) {
        console.error('❌ Error sending WhatsApp notification:', notifyError);
        // Don't throw - notification failure shouldn't break the status update
      }
    } catch (error) {
      console.error('Error updating ad creative status:', error);
      throw error;
    }
  },

  // Delete ad creative
  deleteAdCreative: async (id: string, client?: SupabaseClient<any>) => {
    try {
      set({ loading: true, error: null });

      const { error } = await (client || supabase)
        .from('ad_creatives')
        .delete()
        .eq('id' as any, id as any);

      if (error) throw error;

      set(state => ({
        adCreatives: state.adCreatives.filter(creative => creative.id !== id),
        loading: false
      }));
    } catch (error) {
      console.error('Error deleting ad creative:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to delete ad creative',
        loading: false
      });
    }
  },

  // Archive/unarchive ad creative
  archiveAdCreative: async (id: string, client?: SupabaseClient<any>) => {
    try {
      console.log('archiveAdCreative called with id:', id);
      const creative = get().adCreatives.find(c => c.id === id);
      console.log('Found creative:', creative);
      const newStatus = creative?.status === 'archived' ? 'pending' : 'archived';
      console.log('New status will be:', newStatus);

      const { error } = await (client || supabase)
        .from('ad_creatives')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        } as any)
        .eq('id' as any, id as any);

      if (error) throw error;

      console.log('Database update successful');
      set(state => ({
        adCreatives: state.adCreatives.map(creative =>
          creative.id === id
            ? {
                ...creative,
                status: newStatus as any,
                updatedAt: new Date().toISOString()
              }
            : creative
        )
      }));
      console.log('State updated successfully');
    } catch (error) {
      console.error('Error archiving ad creative:', error);
      throw error;
    }
  },

  // Move video to ad creatives
  handleMoveToAdCreatives: async (submission: VideoSubmission, client?: SupabaseClient<any>) => {
    try {
      // Only allow song-specific videos to be moved to ad creatives
      if (submission.type !== 'song-specific') {
        throw new Error('Only song-specific videos can be moved to Ad Creatives');
      }

      const { data, error } = await (client || supabase)
        .from('ad_creatives')
        .insert({
          artists_id: submission.artistId,
          platform: 'dropbox',
          content: submission.videoUrl,
          status: 'pending',
          video_name: submission.projectName,
          submission_id: submission.id,
          thumbnail_url: submission.thumbnailUrl || null
        } as any)
        .select()
        .single();

      if (error) throw error;
      if (!data) throw new Error('No data returned from ad creative creation');

      const transformedCreative: AdCreative = {
        id: (data as any).id,
        createdAt: (data as any).created_at,
        artists_id: (data as any).artists_id,
        platform: (data as any).platform,
        content: (data as any).content,
        status: (data as any).status,
        rejectionReason: (data as any).rejection_reason,
        updatedAt: (data as any).updated_at,
        videoName: (data as any).video_name,
        mergedInstagramReelUrl: (data as any).merged_instagram_reel_url,
        mergedTiktokAuthCode: (data as any).merged_tiktok_auth_code,
        submissionId: (data as any).submission_id,
        thumbnail_url: (data as any).thumbnail_url,
        instagram_thumbnail_url: (data as any).instagram_thumbnail_url,
        tiktok_thumbnail_url: (data as any).tiktok_thumbnail_url,
        audio_type: (data as any).audio_type,
        original_instagram_url: (data as any).original_instagram_url,
        release_id: (data as any).release_id,
      };

      set(state => ({
        adCreatives: [transformedCreative, ...state.adCreatives]
      }));
    } catch (error) {
      console.error('Error moving to ad creatives:', error);
      throw error;
    }
  },

  // UI Actions
  toggleSidebar: () => set(state => ({ sidebarOpen: !state.sidebarOpen })),
  setSubmissionsPage: (page) => set(state => ({ 
    submissionsPagination: { ...state.submissionsPagination, currentPage: page } 
  })),
  setAdCreativesPage: (page) => set(state => ({ 
    adCreativesPagination: { ...state.adCreativesPagination, currentPage: page } 
  })),
  setSelectedArtistId: (artistId) => set({ selectedArtistId: artistId }),
  setArtists: (artists) => set({ artists }),
  setSubmissions: (submissions) => set({ submissions }),
}));

export { useStore };